/**
 * A fresh random id for a queued mutation, a stored photo, a note card, a pin or a writer tab.
 * NOT FOR SECRETS, TOKENS OR ANYTHING AN ATTACKER MUST NOT GUESS: the last-resort path is Math.random.
 *
 * `crypto.randomUUID` exists only in a secure context (HTTPS) on Safari 15.4+ / Chrome 92+; called bare,
 * it throws on an older iPhone and the action (add a note, a photo, a layout item, Save Survey) fails (#7528).
 * Without it: `getRandomValues`, then Math.random mixed with the clock. Identity, not security.
 * Always a v4-shaped UUID, so every consumer sees one format whichever path ran.
 *
 * KEEP IDENTICAL to the copy in the other package (offline-sdk <-> survey-capture cannot import each other);
 * sales-client/src/lib/noBareRandomUuid.test.ts fails if the two bodies differ.
 */
export function newClientId(): string {
  const c = (globalThis as { crypto?: Crypto }).crypto;
  if (c && typeof c.randomUUID === 'function') return c.randomUUID();
  const bytes = new Uint8Array(16);
  if (c && typeof c.getRandomValues === 'function') c.getRandomValues(bytes);
  else {
    // No crypto object at all: two offline devices must not collide on the same Math.random stream, so fold in the clock.
    const t = Date.now() ^ Math.floor((typeof performance !== 'undefined' ? performance.now() : 0) * 1000);
    for (let i = 0; i < bytes.length; i++) bytes[i] = (Math.floor(Math.random() * 256) ^ (t >>> ((i % 4) * 8))) & 0xff;
  }
  bytes[6] = (bytes[6] & 0x0f) | 0x40; // version 4
  bytes[8] = (bytes[8] & 0x3f) | 0x80; // RFC 4122 variant
  const hex = Array.from(bytes, (b) => b.toString(16).padStart(2, '0')).join('');
  return `${hex.slice(0, 8)}-${hex.slice(8, 12)}-${hex.slice(12, 16)}-${hex.slice(16, 20)}-${hex.slice(20)}`;
}
